import { useState, useEffect, useRef, useCallback, memo } from "react";
import ReactToPrint from "react-to-print";

// prettier-ignore
import { Autocomplete, Paper, SvgIcon, TextField, Tooltip, IconButton, Chip, Box, Checkbox, Button, Stack, 
        Pagination, InputLabel, Select, MenuItem, FormControl, CircularProgress, DialogActions, Dialog, Icon,
        Popover, Typography, Tooltip as MuiTooltip, Switch, DialogTitle, DialogContent, DialogContentText, Modal } from '@mui/material'
// prettier-ignore
import { DateRangeOutlined, Summarize, List, HelpOutline, Refresh, CheckBoxOutlineBlank, CheckBox, //DownloadOutlined,  
        LocalPrintshopOutlined, MarkEmailRead, Markunread, Close, FilterAltOff, Edit } from '@mui/icons-material'
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from "@mui/lab";

// import { saveAs } from "file-saver";
import moment from "moment";

// componentes dependentes (certificar paths)
import { TimelineCardTodosDocumentos } from "./TimelineCardTodosDocuments";
import { TimelineCard } from "./TimelineCard";
import { D, ApostileIcon,  } from "../include";

moment.locale("pt_br");

const LARGE_TIMELINE_THRESHOLD = 1000; // limite ajustável para card todos docs
const LARGE_APOSTILA_THRESHOLD = 150; // limite ajustável para apostilamento

const oicon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

// style para o box dos chips
const style = {
  overflow: "visible",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: "70vw",
  minWidth: "50vw",
  minHeight: "10vh",
  maxHeight: "60vh",
  bgcolor: "background.paper",
  borderRadius: "1rem",
  boxShadow: 24,
  p: 4,
  display: "flex",
  flexDirection: "column",
  padding: "1rem",
  position: "absolute",
};

// utilitaria q elimina fatos sem ocorrencias para comparaçao de mudança
const normalizeSelectedOccurences = (obj = {}) => {
  const normalized = {};

  Object.entries(obj).forEach(([fatoId, occs]) => {
    if (Array.isArray(occs) && occs.length > 0) {
      normalized[fatoId] = [...occs].sort();
    }
  });

  return normalized;
};

// "escudo" para que o React ignore os cards a menos que o array timelineBody efetivamente mude
const MemoizedTimeline = memo(({ timelineBody, onClickCard, onOpenFullDocumentModal }) => {
  if (!timelineBody || timelineBody.length === 0) return null;

  return (
    <Timeline position="alternate">

      {/* --- Card Header (Todos os Documentos) --- */}
      <TimelineItem className="no-print">
        <TimelineSeparator>
          <TimelineDot sx={{ bgcolor: "primary.main" }}>
            <SvgIcon component={List} />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        
        <TimelineContent style={{ width: "40%" }}>
          <Paper 
            elevation={3} 
            onClick={onOpenFullDocumentModal}
            sx={{ 
              maxWidth: "400px", 
              maxHeight: "420px", 
              minHeight: "70px",
              padding: "20px",
              cursor: "pointer",
              display: "inline-flex", 
              justifyContent: "center",
              alignItems: "center",
              color: "blue",
              fontWeight: "bold",
              fontSize: "0.875rem",
              textTransform: "uppercase",
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "action.hover" }
            }}
          >
            Lista de documentos
          </Paper>
        </TimelineContent>
      </TimelineItem>

      {/* --- Renderização dos cards da timeline --- */}
      {timelineBody.map((element) => (
        <TimelineItem key={element.id}>
          <TimelineSeparator>
            <TimelineDot sx={{ bgcolor: "primary.main" }}>
              <SvgIcon component={Summarize} />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          
          <TimelineContent style={{ width: "40%" }}>
            
            <Paper 
              elevation={3} 
              onClick={() => onClickCard(element.doc_id)}
              sx={{ 
                maxWidth: "450px", 
                maxHeight: "420px", 
                minHeight: "70px",
                padding: "12px",
                cursor: "pointer",
                display: "inline-flex",
                flexDirection: "column",
                gap: 1.5, // Espaçamento entre os blocos
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: "action.hover" } // Feedback visual de clique
              }}
            >
              
              {/* CABEÇALHO (Ícones de status) */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%", gap: "6px" }}>
                
                <Tooltip title={element.status} arrow>
                  <Icon style={{ color: "#aaa" }}>{element.status_icon}</Icon>
                </Tooltip>

                <Tooltip title={element.lido ? "Lido" : "Não lido"} arrow>
                  {element.lido ? (
                    <MarkEmailRead style={{ color: "#aaa", fontSize: 26 }} />
                  ) : (
                    <Markunread style={{ color: "#aaa", fontSize: 26 }} />
                  )}
                </Tooltip>

                {element.edt === 1 && (
                  <Tooltip title="Editado" arrow>
                    <Edit style={{ color: "#aaa", fontSize: 26 }}/>
                  </Tooltip>
                )}
              </div>

              {/* DETALHES */}
              <Tooltip title="Detalhes" arrow>
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center", 
                  alignItems: "center",  // Centraliza horizontalmente
                  textAlign: "center",   // Garante alinhamento do texto
                  fontWeight: element.lido ? "normal" : "bold",    // Retorna o bold
                  fontSize: "0.875rem",  // Tamanho original do texto do botão
                  textTransform: "uppercase",
                  color: "blue",
                }}>
                  <span>{element.name}</span>
                  <span>{element.tipoDocumento}</span>
                  <span>{element.fornecedor}</span>
                  <span>{moment(element.data).format("DD/MM/YYYY")}</span>
                </div>
              </Tooltip>

              {/* FATOS */}
              <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "5px", color: "#aaa" }}>
                {element.fatos.map((fato) => {
                  if (fato.icon_name) {
                    return (
                      <Tooltip key={fato.fato_id} title={fato.qualificator} arrow>
                        <Icon style={{ color: "#aaa" }}>{fato.icon_name}</Icon>
                      </Tooltip>
                    );
                  }
                  if (fato.qualificator === "Aprovação" && fato.valor) {
                    return (
                      <Tooltip key={fato.fato_id} title={fato.qualificator} arrow>
                        <Icon style={{ color: "#aaa" }}>
                          {fato.valor?.toLowerCase() === "aprovado" ? "thumb_up" : "thumb_down"}
                        </Icon>
                      </Tooltip>
                    );
                  }
                  return null;
                })}
              </div>

            </Paper>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
});

export const TimelineClass = (props) => {
  // REFS (Substituindo variáveis de instância da classe)
  const printRef = useRef(null);
  const searchNodeRef = useRef(null);
  const hasMounted = useRef(false);
  const isUpdatingTimeline = useRef(false);
  const isReady = useRef(false);
  const searchTimeout = useRef(null);

  // STATE & STATE REF SYNC
  const [state, _setState] = useState({
    dataini: null, datafim: null, dataInicio: "", dataFim: "",
    aproxIniDateMenu: null, aproxFimDateMenu: null,
    showUnread: false, showPending: 0, showEdt: 0,
    filterTimeline: [], timelineFacts: [], timelineOptions: [], search: "",
    timelineFactsToModal: [], timelineFatosName: [], selectTimelineParameters: [],
    factFilters: [], timelineBody: [], currentPage: 1, postsPerPage: 10,
    card: false, closeCard: true, fatos: [],
    ocorrenciasModal: false, factFiltersModal: [], ocorrencias1Modal: false,
    fullDocumentsModal: false, count: 0, debounceTextSearch: "", selectedOccurences: {},
    doc_id: null, showLargeTimelineWarning: false, showConfirmLargeExport: false,
    autocompletefato: false, isRefreshing: false, isLoading: false, isLoadingPdf: false,
    hasFilters: false,
  });

  const stateRef = useRef(state);

  const prevDeps = useRef({
    dataini: null, datafim: null, currentPage: 1, postsPerPage: 10,
    showUnread: false, showPending: 0, showEdt: 0,
    debounceTextSearch: "", fatos: [], selectedOccurences: {}
  });

  const updateState = useCallback((updater) => {
    let nextState;
    if (typeof updater === "function") {
      nextState = { ...stateRef.current, ...updater(stateRef.current) };
    } else {
      nextState = { ...stateRef.current, ...updater };
    }
    stateRef.current = nextState;
    _setState(nextState);
  }, []);

  // =========================================================================
  // FUNÇÃO CENTRALIZADA DE SALVAMENTO
  // =========================================================================
  const saveUserPreferences = async (currentState) => {
    const dataIniFormat = currentState.dataini && moment(currentState.dataini).isValid() ? moment(currentState.dataini).format("DD/MM/YYYY") : "";
    const dataFimFormat = currentState.datafim && moment(currentState.datafim).isValid() ? moment(currentState.datafim).format("DD/MM/YYYY") : "";
    
    const dateParams = {
      dataInicio: dataIniFormat,
      dataFim: dataFimFormat
    };

    const fullPreferences = {
      user_id: props.user.id,
      
      // Chaves que a Timeline governa
      selectTimelineParameters: currentState.fatos ?? [],
      selectFactName: currentState.timelineFatosName ?? [],
      selectDateParameters: dateParams,
      
      // Chaves compartilhadas entre módulos (escalares)
      docspage: Number(currentState.postsPerPage) || 10,
      pendencias: Number(currentState.showPending) || 0,
      unread: Boolean(currentState.showUnread) || false,
      edt: Number(currentState.showEdt) || 0,
    };

    await props.api.post("/user/preferences", fullPreferences).catch(console.error);
  };

  // =========================================================================
  // FUNÇÕES AUXILIARES DE COMPONENTES E GRID
  // =========================================================================
  const getFactsList = async () => {
    try {
      const r = await props.api.get(`Facts`);
      let factsArray = [];
      let options = [];
      r.forEach((d) => {
        factsArray.push({ id: d.id, name: d.qualificator });
        options.push(d.qualificator);
      });
      updateState({ timelineOptions: options, filterTimeline: factsArray });
      return factsArray;
    } catch (error) {
      console.error("Erro ao carregar fatos:", error);
      return [];
    }
  };

  const getTimelineFacts = (value) => {
    const s = stateRef.current;
    const filterTimeline = s.filterTimeline || [];
    
    const idToName = new Map();
    const nameToId = new Map();
    filterTimeline.forEach((el) => {
      idToName.set(el.id, el.name);
      nameToId.set(el.name, el.id);
    });

    const idArray = value.map((v) => nameToId.get(v)).filter(Boolean);

    updateState((prevState) => {
      const prevFatos = prevState.fatos || [];
      const prevSelectedOcc = prevState.selectedOccurences || {};
      const newFatos = [];
      const updatedSelectedOccurences = {};

      idArray.forEach((id) => {
        const nomeFato = idToName.get(id) || id;
        const existing = prevFatos.find((f) => f.fato_id === id);

        if (existing) {
          existing.qualificator = nomeFato;
          newFatos.push(existing);
        } else {
          newFatos.push({ fato_id: id, qualificator: nomeFato, ocorrencias: [] });
        }
        updatedSelectedOccurences[id] = prevSelectedOcc[id] || [];
      });

      return {
        timelineFatosName: value,
        timelineFacts: idArray,
        fatos: newFatos,
        selectedOccurences: updatedSelectedOccurences,
      };
    });
  };

  const getTimelineFactsToOpenModal = (value) => {
    const s = stateRef.current;
    let idArray = [];
    (s.filterTimeline || []).forEach((element) => {
      if (value.find((obj) => obj === element.name)) idArray.push(element.id);
    });
    
    updateState({ timelineFactsToModal: idArray });

    if (idArray.length > 0) {
      props.api.get(`Timeline/Fatos/${JSON.stringify(idArray)}`).then((r) => {
        updateState({ factFiltersModal: r, ocorrencias1Modal: true });
      }).catch(console.error);
    }
  };


  const getTimelineBody = async () => {
    if (isUpdatingTimeline.current) return;
    
    const s = stateRef.current;
    const filtersFatos = s.fatos.filter((f) => Array.isArray(f.ocorrencias) && f.ocorrencias.length > 0);

    if (filtersFatos.length === 0) {
      updateState({ timelineBody: [], count: 0, debounceTextSearch: "", isRefreshing: false, isLoading: false });
      return;
    }

    isUpdatingTimeline.current = true;
    updateState({ isRefreshing: true, isLoading: true });

    const queryParams = {
      filters: JSON.stringify(filtersFatos), textFilter: s.debounceTextSearch,
      page: s.currentPage, pageSize: s.postsPerPage, dataInicio: s.dataInicio, dataFim: s.dataFim,
      orderBy: JSON.stringify(["Data Aproximada", "desc"]), 
      showUnread: s.showUnread, showPend: s.showPending, showEdt: s.showEdt,
    };
    const queryStringParams = new URLSearchParams(queryParams);

    try {
      const { data, count } = await props.api.get(`timeline?${queryStringParams.toString()}`);

      const body = [...data].map((e) => ({
        id: e.document_id, doc_id: e.document_id, date: e.created_at, dtaprox: e.dtaprox,
        data: e.dtaprox, fatos: e.fatos_do_documento, status: e.status, lido: e.lido, edt: e.edt,
      }));

      const statusMap = { "processado": "check_circle", "não processado": "sync", "em processamento": "sync_disabled" };

      for (let i = 0; i < body.length; i++) {
        let dt = body[i].dtaprox;
        body[i].dtaprox = Date.parse(dt ? dt.split("/").reverse().join("/") : body[i].created_at);
        for (const item of body[i].fatos) {
          if (item.qualificator === "Nome de Embarcação") body[i].name = item.valor;
          else if (item.qualificator === "Tipo de Documento") body[i].tipoDocumento = item.valor;
          else if (item.qualificator === "Fornecedor") body[i].fornecedor = item.valor;
        }
        body[i].status_icon = statusMap[body[i].status] || "help_outline";
      }

      updateState({
        timelineBody: body, count: +count,
        ...(body.length === 0 && { currentPage: 1 }),
        isRefreshing: false, isLoading: false,
      });
    } catch (err) {
      console.error("Erro ao processar a timeline !", err);
      updateState({ timelineBody: [], count: 0, isRefreshing: false, isLoading: false });
    } finally {
      isUpdatingTimeline.current = false;
    }
  };

  const triggerTimelineFetch = useCallback(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { getTimelineBody(); }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // =========================================================================
  // CICLO DE VIDA (Hooks)
  // =========================================================================
  
  // INICIALIZAÇÃO (Mount)
  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;

    const init = async () => {
      // Aguarda lista de domínios antes de ler preferências
      await getFactsList();

      // Busca preferências globais pelo Cofre Cego
      const userPrefs = await props.api.get(`/user/preferences?user_id=${props.user.id}`);

      // Parse e Injeção de Datas
      const dateFilter = { dataini: null, datafim: null, dataInicio: "", dataFim: "" };
      
      if (userPrefs?.selectDateParameters) {
        let dateParamsObj = userPrefs.selectDateParameters;
        if (typeof dateParamsObj === 'string') {
          try { dateParamsObj = JSON.parse(dateParamsObj); } 
          catch { dateParamsObj = {}; }
        }

        if (dateParamsObj.dataInicio && dateParamsObj.dataInicio.trim() !== "") {
          const iniMoment = moment(dateParamsObj.dataInicio, "DD/MM/YYYY");
          if (iniMoment.isValid()) {
            dateFilter.dataini = iniMoment;
            dateFilter.dataInicio = iniMoment.format("DD/MM/YYYY");
          }
        }
        if (dateParamsObj.dataFim && dateParamsObj.dataFim.trim() !== "") {
          const fimMoment = moment(dateParamsObj.dataFim, "DD/MM/YYYY");
          if (fimMoment.isValid()) {
            dateFilter.datafim = fimMoment;
            dateFilter.dataFim = fimMoment.format("DD/MM/YYYY");
          }
        }
      }

      // Injeção de Fatos e Configurações da Timeline
      const tlparms = userPrefs?.selectTimelineParameters || [];
      const selectedOccurences = {};
      const fatos = tlparms.map((item) => {
        selectedOccurences[item.fato_id] = item.ocorrencias;
        return { fato_id: item.fato_id, qualificator: item.qualificator, ocorrencias: item.ocorrencias };
      });

      const selectFactName = userPrefs?.selectFactName || [];
      const selectFatosID = tlparms.map(f => f.fato_id);

      // Atualiza o Estado
      updateState({
        ...dateFilter,
        timelineFacts: selectFatosID,
        timelineFatosName: selectFactName,
        selectTimelineParameters: tlparms,
        fatos: fatos,
        selectedOccurences: selectedOccurences,
        showPending: Number(userPrefs?.pendencias) || 0,
        showUnread: Boolean(userPrefs?.unread) || false,
        showEdt: Number(userPrefs?.edt) || 0,
        postsPerPage: Number(userPrefs?.docspage) || 10,
      });

      // 6. Sincroniza PrevDeps
      const s = stateRef.current;
      prevDeps.current = {
        dataini: s.dataini, datafim: s.datafim, currentPage: s.currentPage, postsPerPage: s.postsPerPage,
        showUnread: s.showUnread, showPending: s.showPending, showEdt: s.showEdt, 
        fatos: s.fatos, selectedOccurences: s.selectedOccurences, debounceTextSearch: s.debounceTextSearch,
      };

      isReady.current = true;

      // 7. Dispara a grid
      if (tlparms.length > 0) {
        getTimelineBody();
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ATUALIZAÇÃO REATIVA (Update)
  useEffect(() => {
    if (!isReady.current) return;

    let shouldFetchGrid = false;
    let shouldSavePrefs = false;
    const p = prevDeps.current;
    const s = state;

    // 1. Mudança nas Datas
    if (p.dataini !== s.dataini || p.datafim !== s.datafim) {
      shouldFetchGrid = true;
      shouldSavePrefs = true;
      
      const dataIniFormat = s.dataini && moment(s.dataini).isValid() ? moment(s.dataini).format("DD/MM/YYYY") : "";
      const dataFimFormat = s.datafim && moment(s.datafim).isValid() ? moment(s.datafim).format("DD/MM/YYYY") : "";
      
      updateState({ dataInicio: dataIniFormat, dataFim: dataFimFormat });
    }

    // Mudança nos parâmetros de visualização
    if (p.currentPage !== s.currentPage || p.postsPerPage !== s.postsPerPage ||
        p.showUnread !== s.showUnread || p.showPending !== s.showPending || p.showEdt !== s.showEdt)
    {
      shouldFetchGrid = true;
      shouldSavePrefs = true; // Escalares alterados, hora de salvar.
    }

    // Mudança no Texto de Busca
    if (p.debounceTextSearch !== s.debounceTextSearch) {
      shouldFetchGrid = true;
    }

    // Mudança nos Fatos e Ocorrências
    if (p.fatos !== s.fatos) {
      shouldSavePrefs = true; // Fatos alterados, hora de salvar.
      
      const prevOccStr = JSON.stringify(normalizeSelectedOccurences(p.selectedOccurences));
      const currOccStr = JSON.stringify(normalizeSelectedOccurences(s.selectedOccurences));
      
      if (prevOccStr !== currOccStr) {
        shouldFetchGrid = true;
      }
    }

    // Se houve alteração de preferência persistente, invoca o Cofre Cego
    if (shouldSavePrefs) {
      // Passa o estado atual mesclado com as formatações ativas de data
      const currentStateToSave = {
        ...s,
        dataInicio: s.dataini && moment(s.dataini).isValid() ? moment(s.dataini).format("DD/MM/YYYY") : "",
        dataFim: s.datafim && moment(s.datafim).isValid() ? moment(s.datafim).format("DD/MM/YYYY") : ""
      };
      saveUserPreferences(currentStateToSave);
    }

    // Se as mudanças afetam o layout dos resultados, puxa do banco
    if (shouldFetchGrid)  triggerTimelineFetch();

    // Atualiza tracking das dependencias prévias
    prevDeps.current = {
      dataini: s.dataini, datafim: s.datafim, currentPage: s.currentPage, postsPerPage: s.postsPerPage,
      showUnread: s.showUnread, showPending: s.showPending, showEdt: s.showEdt, 
      fatos: s.fatos, selectedOccurences: s.selectedOccurences, debounceTextSearch: s.debounceTextSearch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dataini, state.datafim, state.currentPage, state.postsPerPage, 
    state.showUnread, state.showPending, state.showEdt,
    state.debounceTextSearch, state.fatos, state.selectedOccurences
  ]);

  // =========================================================================
  // HANDLERS GERAIS E RENDERS
  // =========================================================================
  const handleDebouncedText = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateState({ debounceTextSearch: value, currentPage: 1 });
    }, 500);
  };

  const handleMergePDFsAndDownload = () => {
    updateState({ isLoadingPdf: true });

    const s = stateRef.current;
    const newFatos = s.fatos
      .filter((object, index) => index === s.fatos.findIndex((value) => object.fato_id === value.fato_id))
      .filter((item) => item.ocorrencias.length !== 0);
    
    const queryParams = {
      filters: JSON.stringify(newFatos), textFilter: s.debounceTextSearch,
      dataInicio: s.dataInicio, dataFim: s.dataFim, 
      showUnread: s.showUnread, showPend: s.showPending, showEdt: s.showEdt
    };
    
    props.api.get(`/timeline/export-pdf?${new URLSearchParams(queryParams).toString()}`, { responseType: "arraybuffer", transformResponse: (r) => r })
      .then((response) => {
        const blob = new Blob([response], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = `timeline_export.pdf`;
        link.click();
        URL.revokeObjectURL(fileURL); // Adicionado para evitar memory leaks de URL Blob
      })
      .catch((error) => {
        console.error("Erro ao gerar PDF:", error);
        if (props.alert) props.alert("Erro ao processar o PDF.");
      })
      .finally(() => updateState({ isLoadingPdf: false }));
  };

  const cleanFilter = () => {
    updateState({ dataini: null, datafim: null, dataInicio: "", dataFim: "", showUnread: false, showPending: 0, showEdt: 0, currentPage: 1 });
  };

  const computeHasFilters = (currentState = state) => {
    const { dataini, datafim, showUnread, showPending, showEdt } = currentState;
    return !!dataini || !!datafim || showUnread || showPending !== 0 || showEdt !== 0;
  };

  const totalPages = Math.ceil(state.count / state.postsPerPage);
  const filtersToRenderForModal = state.factFiltersModal && state.factFiltersModal.length > 0 ? state.factFiltersModal : state.factFilters || [];
  const temFiltro = computeHasFilters();

  return (
    <D className="col e aF scD" style={{ padding: "1rem 1rem 0.5rem 1rem", position: "sticky", top: 24, zIndex: 1000, backgroundColor: "white" }}>
      <D className="col w aF" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
        
        {/* ===== BLOCO ESQUERDO: datas + switches ===== */}
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "1rem" }}>
          <D className="row w aC" style={{ width: "auto", marginLeft: 0 }}>
            <D className="row aC" style={{ background: "#eeeeee", borderRadius: "0.5rem", padding: "0px 4px" }}>
              
              {/* DATA INICIAL */}
              <MuiTooltip title={"Data inicial " + state.dataInicio} arrow>
                <IconButton
                  onClick={(e) => updateState({ aproxIniDateMenu: e.currentTarget })}
                  color={!!state.dataini ? "primary" : "default"}
                >
                  <DateRangeOutlined />
                </IconButton>
              </MuiTooltip>
              <Popover
                open={!!state.aproxIniDateMenu} anchorEl={state.aproxIniDateMenu}
                onClose={() => updateState({ aproxIniDateMenu: null })}
                disableRestoreFocus disableEnforceFocus disableAutoFocus
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop" value={state.dataini || null} maxDate={state.datafim || undefined}
                    onChange={(v) => {
                      const valid = v && v.isValid() ? v : null;
                      updateState((prev) => ({
                        dataini: valid, aproxIniDateMenu: false,
                        datafim: valid && prev.datafim && moment(valid).isAfter(prev.datafim) ? valid : prev.datafim,
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <DialogActions>
                  <Button variant="outlined" onClick={() => updateState({ dataini: null, aproxIniDateMenu: null })} color="primary">Limpar Data</Button>
                </DialogActions>
              </Popover>

              {/* DATA FINAL */}
              <MuiTooltip title={"Data final " + state.dataFim} arrow>
                <IconButton
                  onClick={(e) => updateState({ aproxFimDateMenu: e.currentTarget })}
                  color={!!state.datafim ? "primary" : "default"}
                >
                  <DateRangeOutlined />
                </IconButton>
              </MuiTooltip>
              <Popover
                open={!!state.aproxFimDateMenu} anchorEl={state.aproxFimDateMenu}
                onClose={() => updateState({ aproxFimDateMenu: null })}
                disableRestoreFocus disableEnforceFocus disableAutoFocus
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop" value={state.datafim || null} minDate={state.dataini || undefined}
                    onChange={(v) => {
                      const valid = v && v.isValid() ? v : null;
                      updateState((prev) => ({
                        datafim: valid, aproxFimDateMenu: false,
                        dataini: valid && prev.dataini && moment(valid).isBefore(prev.dataini) ? valid : prev.dataini,
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <DialogActions>
                  <Button variant="outlined" onClick={() => updateState({ datafim: null, aproxFimDateMenu: null })} color="primary">Limpar Data</Button>
                </DialogActions>
              </Popover>
            </D>
          </D>

          {/* SWITCHES E BOTOES LIMPAR/REFRESH */}
          <Box sx={{ display: "inline-block", backgroundColor: "#eeeeee", borderRadius: "0.5rem", padding: "4px 8px 0px 0px" }}>
            <Stack direction="row" sx={{ alignItems: "center", marginLeft: "8px" }}>
              <Typography variant="body2">Todos</Typography>
              <Switch checked={state.showUnread} onChange={(e) => updateState({ showUnread: e.target.checked })} />
              <Typography variant="body2">Não lidos</Typography>
            </Stack>
          </Box>

          <Box sx={{ display: "inline-block", backgroundColor: "#eeeeee", borderRadius: "0.5rem", padding: "4px 8px 0px 0px" }}>
            <Stack direction="row" sx={{ alignItems: "center", marginLeft: "8px" }}>
              <Typography variant="body2">Todos</Typography>
              <Switch checked={!!state.showEdt} onChange={(e) => updateState({ showEdt: e.target.checked ? 1 : 0 })} />
              <Typography variant="body2">Editados</Typography>
            </Stack>
          </Box>

          <Box sx={{ display: "inline-block", backgroundColor: "#eeeeee", borderRadius: "0.5rem", padding: "4px 8px 0px 0px" }}>
            <Stack direction="row" sx={{ alignItems: "center", marginLeft: "8px" }}>
              <Typography variant="body2">Todos</Typography>
              <Switch checked={!!state.showPending} onChange={(e) => updateState({ showPending: e.target.checked ? 1 : 0 })} />
              <Typography variant="body2">C/ pendências</Typography>
            </Stack>
          </Box>

          {temFiltro && (
            <Tooltip title="Limpar filtros" arrow>
              <IconButton onClick={cleanFilter} ><FilterAltOff color="action" /></IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Atualizar" arrow>
            <span><IconButton size="small" onClick={getTimelineBody} disabled={state.isRefreshing}><Refresh /></IconButton></span>
          </Tooltip>
        </Box>

        {/* ===== BLOCO DIREITO: Print / apostila ===== */}
        {Number(state.count) > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.0rem" }}>
            {/* botao imprimir linha do tempo */}
            <ReactToPrint
              trigger={() => (<Tooltip title="Imprimir página da Linha do Tempo" arrow><IconButton><LocalPrintshopOutlined /></IconButton></Tooltip>)}
              content={() => printRef.current}
              onBeforeGetContent={() => {
                if (!state.timelineBody || state.timelineBody.length === 0) {
                  if (props.alert) props.alert("Não há Linha do Tempo a imprimir !");
                  return Promise.reject();
                }
              }}
            />

            {/* botao gerar apostila de documentos */}
            <Tooltip title="Exportar Apostila de PDFs" arrow>
              <IconButton
                onClick={() => {
                  if (state.count > LARGE_APOSTILA_THRESHOLD) updateState({ showConfirmLargeExport: true });
                  else handleMergePDFsAndDownload();
                }}
                disabled={state.isLoadingPdf} sx={{ color: "grey.600" }}
              >
                {state.isLoadingPdf ? <CircularProgress size={20} /> : <ApostileIcon />}
              </IconButton>
            </Tooltip>

            {/* Modal Confirmar Exportação Longa */}
            <Dialog open={state.showConfirmLargeExport} onClose={() => updateState({ showConfirmLargeExport: false })}>
              <DialogTitle>Atenção</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  A linha do tempo contém <b>{new Intl.NumberFormat("pt-BR").format(state.count)}</b> documentos.<br />
                  A exportação pode demorar vários minutos e gerar arquivo muito grande.<br />A operação <b>não</b> poderá ser cancelada antes de finalizada.<br /><br />
                  Deseja continuar mesmo assim?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => updateState({ showConfirmLargeExport: false })}>Cancelar</Button>
                <Button onClick={() => { updateState({ showConfirmLargeExport: false }); handleMergePDFsAndDownload(); }} variant="contained" color="primary">Continuar</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </D>

      {/* -------2A. LINHA: FILTROS ---------*/}
      <Box sx={{ display: "block", displayPrint: "none", backgroundColor: "white", padding: "0", borderRadius: "1rem", marginTop: "18px", marginBottom: "0px" }}>
        <Stack direction="row" spacing={0}>
          <Autocomplete
            value={state.timelineFatosName || []} multiple={true} sx={{ width: "70%", paddingBottom: "0.5rem" }}
            getOptionLabel={(options) => options || null}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={oicon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {option}
              </li>
            )}
            onChange={(e, value) => {
              const prev = state.timelineFatosName || [];
              const added = value.find((v) => !prev.includes(v));
              getTimelineFacts(value);
              if (added) getTimelineFactsToOpenModal([added]);
            }}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip variant="outlined" label={option} onClick={() => getTimelineFactsToOpenModal([option])} {...getTagProps({ index })} />
            ))}
            options={state.timelineOptions} isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <div>
                <TextField {...params} label={<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>Fatos e ocorrências<Tooltip title="Selecione os fatos e ocorrências desejadas"><HelpOutline /></Tooltip></div>} />
              </div>
            )}
          />
          <Tooltip title={<>Palavras a filtrar no conjunto de<br />documentos selecionados</>} arrow followCursor>
            <TextField
              sx={{ marginLeft: "2px" }} variant="outlined" defaultValue={state.search} ref={searchNodeRef}
              onChange={(e) => handleDebouncedText(e.target.value)} style={{ paddingBottom: "0.5rem", width: "50%" }}
              label={<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>Filtrar<HelpOutline fontSize="small" /></div>}
            />
          </Tooltip>
        </Stack>
      </Box>

      {/* ---- 3a LINHA: PAGINAÇÃO ---------- */}
      <Stack display="" sx={{ "@media print": { displayPrint: "none" }, position: "sticky", top: 0, zIndex: 1000, backgroundColor: "white", padding: "0" }}>
        <div style={{ display: "flex", alignItems: "center", margin: 0, padding: 0 }}>
          {/* -- botao lista ocorrencias selecionadas -- */}
          <Button
            sx={{ marginRight: "10px" }} variant="outlined" disabled={state.fatos.length === 0}
            onClick={() => {
              updateState({ 
                factFiltersModal: state.fatos, 
                ocorrenciasModal: state.fatos.length > 0 
              });
            }}
          >
            Ocorrências Selecionadas
          </Button>

          {/* numero de documentos da timeline */}
          <div style={{ flex: 0, textAlign: "left", margin: "0 4rem", whiteSpace: "nowrap" }}>
            {state.fatos?.some(fato => fato.ocorrencias?.length > 0) && (
              Number(state.count) === 0 
                ? "Nenhum documento encontrado" 
                : `${new Intl.NumberFormat("pt-BR").format(state.count)} ${Number(state.count) === 1 
                  ? "Documento encontrado" 
                  : "Documentos encontrados"}`
            )}
          </div>

          <div style={{ display: "flex", flex: 1, justifyContent: "flex-end", alignItems: "center", gap: "0px" }}>
            <FormControl sx={{ m: 1, minWidth: 100 }} size="small">
              <InputLabel id="page-size-select-label" htmlFor="page-size-select-input">
                Documentos
              </InputLabel>
              <Select 
                label="Documentos" 
                labelId="page-size-select-label" // <-- 2. Avisamos ao Select quem é o label dele
                id="page-size-select"
                name="pageSize"
                value={state.postsPerPage} 
                onChange={(e) => updateState({ postsPerPage: e.target.value })}
                inputProps={{
                  id: 'page-size-select-input',  // Injeta o ID diretamente no <input> escondido
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination count={totalPages} page={state.currentPage} onChange={(_, p) => updateState({ currentPage: p })} size="medium" variant="outlined" shape="rounded" siblingCount={1} boundaryCount={1} />
          </div>
        </div>
      </Stack>

      {/* ---------- TIMELINE CONTAINER ---------- */}
      <D className="timeline-container" style={{ height: "calc(100vh - 220px)", overflowY: "scroll", padding: "1rem 0 2rem 0", boxSizing: "border-box", msOverflowStyle: "none", scrollbarWidth: "none" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {state.isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}><CircularProgress /></div>
          ) : (
            <div ref={printRef}>
              <MemoizedTimeline 
                timelineBody={state.timelineBody} 
                onClickCard={(e) => updateState({ card: true, doc_id: e })} 
                onOpenFullDocumentModal={() => {
                  if (state.count > LARGE_TIMELINE_THRESHOLD) updateState({ showLargeTimelineWarning: true });
                  else updateState({ fullDocumentsModal: true });
                }} 
              />
            </div>
          )}
        </div>
      </D>

      {/* MODAIS RESTANTES (Ocorrências, Cards, Warnings) */}
      {state.ocorrenciasModal && (
        <Modal open={state.ocorrenciasModal} onClose={() => updateState({ ocorrenciasModal: false })}>
          <Box style={style} sx={{ bgcolor: "white" }}>
            <header style={{ paddingBottom: "6px", borderBottom: "1px solid gray", display: "flex", justifyContent: "space-between" }}>
              <div><h2 style={{ padding: 0, margin: 0, color: "#3f3f3f" }}>Fatos & Ocorrências Selecionadas</h2></div>
              <div style={{ textAlign: "end", padding: 0, margin: 0 }}><Close onClick={() => updateState({ ocorrenciasModal: false })} sx={{ ":hover": { color: "grey", backgroundColor: "white" } }} /></div>
            </header>
            <div style={{ display: "flex", flexDirection: "column", position: "relative", overflowY: "auto", height: "calc(100% - 72px)", paddingTop: "16px", gap: "12px" }}>
              {filtersToRenderForModal.map((element) => (
                <Box key={element.fato_id} sx={{ border: "1px solid #ccc", borderRadius: "8px", padding: "16px 12px 12px 12px", marginBottom: "12px", position: "relative" }}>
                  <div style={{ position: "absolute", top: "-10px", left: "12px", backgroundColor: "white", padding: "0 6px", fontWeight: "400", fontSize: "0.9rem", color: "#000" }}>
                    {element.qualificator || element.fato_id}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                    {(element.ocorrencias || []).map((occ) => (
                      <Box key={occ} sx={{ padding: "4px 8px", backgroundColor: "transparent", borderRadius: "16px", fontSize: "0.875rem", color: "black", border: "1px solid #ccc" }}>{occ}</Box>
                    ))}
                  </div>
                </Box>
              ))}
            </div>
            <DialogActions><Button style={{ width: "100px" }} variant="outlined" color="primary" onClick={() => updateState({ ocorrenciasModal: false })}>OK</Button></DialogActions>
          </Box>
        </Modal>
      )}

      {state.card && 
        <TimelineCard 
          doc_id={state.doc_id} 
          api={props.api} 
          stateCard={state.card} 
          onCloseCard={() => updateState({ card: false })} 
        />}
      
      {state.fullDocumentsModal && (
        <TimelineCardTodosDocumentos
          api={props.api} showModal={state.fullDocumentsModal} onCloseCard={() => updateState({ fullDocumentsModal: false })}
          data={state.timelineBody} onSelectRow={(e) => updateState({ card: true, doc_id: e })}
          dataInicio={state.dataInicio} dataFim={state.dataFim} fatos={state.fatos}
          textFilter={state.debounceTextSearch} 
          showUnread={state.showUnread} 
          showPend={state.showPending} 
          showEdt={state.showEdt}
          alert={props.alert}
        />
      )}

      {state.showLargeTimelineWarning && (
        <Dialog open={state.showLargeTimelineWarning} onClose={() => updateState({ showLargeTimelineWarning: false })}>
          <DialogTitle>Atenção</DialogTitle>
          <DialogContent>
            <DialogContentText>
              A linha do tempo contém <b>{new Intl.NumberFormat("pt-BR").format(state.count)}</b> documentos.<br />
              Isso pode demorar para carregar.<br /><br />
              Deseja continuar mesmo assim?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => updateState({ showLargeTimelineWarning: false })}>Cancelar</Button>
            <Button onClick={() => updateState({ showLargeTimelineWarning: false, fullDocumentsModal: true })} variant="contained" color="primary">Continuar</Button>
          </DialogActions>
        </Dialog>
      )}

      {state.ocorrencias1Modal && (
        <Modal open={state.ocorrencias1Modal} onClose={() => updateState({ ocorrencias1Modal: false })}>
          <Box style={style} sx={{ bgcolor: "white" }}>
            <header style={{ paddingBottom: "6px", borderBottom: "1px solid gray", flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
              <div><h2 style={{ padding: 0, margin: 0, color: "#3f3f3f" }}>Ocorrências Selecionadas</h2></div>
              <div style={{ textAlign: "end", padding: 0, margin: 0 }}><Close onClick={() => updateState({ ocorrencias1Modal: false })} sx={{ ":hover": { color: "grey", backgroundColor: "white" } }} /></div>
            </header>
            <div style={{ display: "flex", flexDirection: "column", position: "relative", height: "calc(100% - 72px)", overflowY: "auto" }}>
              {(state.factFiltersModal || state.factFilters)?.map((element) => (
                <Autocomplete
                  key={element.fato_id} multiple id={`checkboxes-tags-${element.fato_id}`} options={element.quantificadores || []}
                  value={state.selectedOccurences[element.fato_id] || []} disableCloseOnSelect
                  onOpen={() => updateState({ autocompletefato: true })}
                  onClose={(event, reason) => { if (reason !== "selectOption") updateState({ autocompletefato: false }); }}
                  getOptionLabel={(option) => option || ""}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}><Checkbox icon={oicon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />{option}</li>
                  )}
                  style={{ width: "100%", paddingBottom: "10px", paddingTop: "15px", marginTop: "0px" }}
                  renderInput={(params) => <TextField {...params} label={element.qualificator} placeholder="" />}
                  onChange={(e, value) => {
                    const hasAll = value.includes("Todos");
                    const newSelected = hasAll ? (element.quantificadores || []) : value.filter((item) => item !== "Todos");
                    
                    updateState(prev => {
                      const updatedOccurences = { ...prev.selectedOccurences };
                      if (newSelected.length > 0) updatedOccurences[element.fato_id] = newSelected;
                      else delete updatedOccurences[element.fato_id];
                      
                      const updatedFatos = (prev.fatos || []).map((f) => f.fato_id === element.fato_id ? { ...f, ocorrencias: newSelected } : f);
                      if (!updatedFatos.find((f) => f.fato_id === element.fato_id)) {
                        updatedFatos.push({ fato_id: element.fato_id, qualificator: element.qualificator, ocorrencias: newSelected });
                      }
                      return { selectedOccurences: updatedOccurences, fatos: updatedFatos };
                    });
                  }}
                />
              ))}
            </div>
            <DialogActions><Button style={{ width: "100px" }} variant="outlined" color="primary" onClick={() => updateState({ ocorrencias1Modal: false })}>OK</Button></DialogActions>
          </Box>
        </Modal>
      )}
    </D>
  );
};
